import type { VoicemailMessageInput, VoicemailMessageUpdateInput } from "@/lib/queries";
import type { AudioFile, VoicemailMessage } from "@/lib/types";
import { pickedAudio, storeAudioUuid, type PickedAudio } from "./audio.js";
import { mediaProblem } from "./campaign.js";

/**
 * The voicemail library as the console shows and edits it (ADR-019): the messages a rep leaves on a
 * lead's voicemail, and the arithmetic between the library's form and `POST`/`PUT
 * /voicemail-messages`.
 *
 * The rules are core-api's, in `schemas.py`: the slug is `_VOICEMAIL_SLUG_RE`, the languages
 * `_check_language`, the name's bound `VoicemailMessageCreate`, and what a message may play
 * `_check_voicemail_media` — a file from the audio store by its URL (`lib/audio.ts`), or, from before
 * the store, a sound file on the switch, a `local_stream://` or a `silence_stream://`. That a store
 * file is the tenant's own is checked on the server only. The server decides all of it; the copies
 * here keep a save it would refuse off the screen and say why first. Pure, for
 * `test/voicemail.test.ts`.
 */

/** `_VOICEMAIL_SLUG_RE`: what a drop names the message by. */
export const VOICEMAIL_SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;
export const NAME_MAX = 200;

/** `_check_language`: the languages a message can be marked with. */
export const VOICEMAIL_LANGUAGES = ["en", "es"] as const;

const LANGUAGE: Record<string, string> = { en: "English", es: "Spanish" };

/** A language in words, or null for none. A code this console does not know is shown as the phone
 *  shows every code, in capitals. */
export function languageText(language: string | null): string | null {
  if (!language) return null;
  return LANGUAGE[language] ?? language.toUpperCase();
}

/** `_SILENCE_RE`: silence the switch makes itself, for trying a drop with nothing recorded. */
const SILENCE = /^silence_stream:\/\/\d{1,6}$/;

/**
 * Why a message cannot play this, or null when it can. Empty is refused: a line's or a campaign's
 * sound falls back on a default, and a message has nothing to fall back on. A value from before the
 * audio store passes, as the server lets it; anything else is not a file from the store.
 */
export function voicemailMediaProblem(value: string): string | null {
  const v = value.trim();
  if (v === "") return "Choose the recording it plays";
  return SILENCE.test(v) || mediaProblem(v) === null ? null : "Choose a file from the audio store";
}

// --- the table ---------------------------------------------------------------------------------

/** What a message plays, as the library's table names it. */
export type MessageRecording =
  | PickedAudio
  /** A store URL while the audio store has not been read, or could not be: a file the page cannot
   *  name yet, which is not the same as a file gone from the store. */
  | { kind: "unread"; uuid: string };

/**
 * What a message plays, named from the audio store as a setting's picker names it (`pickedAudio`).
 * `files` is undefined until the store has been read, and a store URL is called missing only after
 * that: a table that said "no longer in the audio store" on every row while the list loaded would be
 * saying something untrue.
 */
export function messageRecording(
  media: string,
  files: readonly AudioFile[] | undefined,
): MessageRecording {
  const uuid = storeAudioUuid(media);
  if (uuid !== null && files === undefined) return { kind: "unread", uuid };
  return pickedAudio(media, files ?? []);
}

/** Which messages the library shows. Only an admin is sent the retired ones to choose between. */
export const LIBRARY_SHOWS = ["all", "active", "retired"] as const;
export type LibraryShow = (typeof LIBRARY_SHOWS)[number];

/**
 * The library as the page lists it: the server's order (by name), narrowed to what is shown, retired
 * messages last. Below admin core-api sends only the active ones whatever is asked, so there is
 * nothing to narrow.
 */
export function libraryRows<T extends Pick<VoicemailMessage, "active">>(
  messages: readonly T[],
  show: LibraryShow,
): T[] {
  const shown = messages.filter((m) => show === "all" || m.active === (show === "active"));
  return shown.sort((a, b) => Number(b.active) - Number(a.active));
}

// --- the form ----------------------------------------------------------------------------------

/** The library's form. No language is `""`; the recording is the URL the picker copied. */
export interface VoicemailMessageValues {
  slug: string;
  name: string;
  language: string;
  /** A lead stage (ADR-061), or "" for the tenant's general message. */
  stage: string;
  media: string;
}

export const NEW_VOICEMAIL_MESSAGE: VoicemailMessageValues = {
  slug: "",
  name: "",
  language: "",
  stage: "",
  media: "",
};

export function voicemailMessageValues(m: VoicemailMessage): VoicemailMessageValues {
  return {
    slug: m.slug,
    name: m.name,
    language: m.language ?? "",
    stage: m.stage ?? "",
    media: m.media,
  };
}

/** What `POST /voicemail-messages` takes: trimmed, with no language as null. A new message is active,
 *  as the server's default makes it; retiring is the list's own action. */
export function newVoicemailMessagePayload(
  values: VoicemailMessageValues,
): Required<Omit<VoicemailMessageInput, "tenant_id" | "active">> {
  return {
    slug: values.slug.trim(),
    name: values.name.trim(),
    language: values.language || null,
    stage: values.stage || null,
    media: values.media.trim(),
  };
}

/**
 * The fields an edit changed, as `PUT /voicemail-messages/{id}` takes them, and nothing else. Never
 * the slug — a drop names the message by it, and core-api has no way to rename one — and never
 * `active`, so saving a name does not restore a message somebody retired while the form was open. A
 * recording goes out only when it changed: core-api checks every recording it is sent against the
 * audio store, and fixing a name is no reason to run that check.
 */
export function voicemailMessageChanges(
  values: VoicemailMessageValues,
  m: VoicemailMessage,
): VoicemailMessageUpdateInput {
  const next = newVoicemailMessagePayload(values);
  const out: VoicemailMessageUpdateInput = {};
  if (next.name !== m.name) out.name = next.name;
  if (next.language !== (m.language || null)) out.language = next.language;
  if (next.stage !== (m.stage || null)) out.stage = next.stage;
  if (next.media !== m.media) out.media = next.media;
  return out;
}
